// frontend/src/pages/RegisterPage.tsx
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { UserPlus, Mail, Lock, Loader2, User } from 'lucide-react';
import apiClient from '@/api';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('[DEBUG RegisterPage] Form submitted. handleSubmit triggered.');

    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    console.log('[DEBUG RegisterPage] Initial states set. Validating inputs...');

    if (password !== confirmPassword) {
      console.log('[DEBUG RegisterPage] Validation failed: Passwords do not match.');
      setError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    if (password.length < 8) {
      console.log('[DEBUG RegisterPage] Validation failed: Password too short.');
      setError("Password must be at least 8 characters long.");
      setIsSubmitting(false);
      return;
    }

    console.log('[DEBUG RegisterPage] Validations passed.');

    try {
      const registrationData = {
        email,
        password,
        name: name || null,
      };
      console.log('[DEBUG RegisterPage] Attempting API call to /users/register with data:', registrationData);
      
      // Ensure VITE_API_BASE_URL is accessible and correct
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      console.log('[DEBUG RegisterPage] VITE_API_BASE_URL:', baseUrl);
      if (!baseUrl) {
          console.error('[DEBUG RegisterPage] VITE_API_BASE_URL is not set!');
          setError('API configuration error. Please contact support.');
          setIsSubmitting(false);
          return;
      }

      const response = await apiClient.post('/users/register', registrationData);
      console.log('[DEBUG RegisterPage] API call successful. Response:', response);

      setSuccessMessage('Registration successful! Redirecting to login...');
      
      setName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        navigate('/login?registration=success');
      }, 2000);

    } catch (err: any) {
      console.error('[DEBUG RegisterPage] API call failed. Error object:', err);
      if (err.response && err.response.data && err.response.data.detail) {
        console.error('[DEBUG RegisterPage] Backend error detail:', err.response.data.detail);
        setError(err.response.data.detail);
      } else if (err.request) {
        console.error('[DEBUG RegisterPage] Network error or no response from server:', err.request);
        setError('Network error or no response from server. Please check your connection and the backend server status.');
      } 
      else {
        console.error('[DEBUG RegisterPage] Generic error:', err.message);
        setError('An unexpected error occurred during registration. Please try again.');
      }
    } finally {
      console.log('[DEBUG RegisterPage] handleSubmit finished. Setting isSubmitting to false.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] md:min-h-[calc(100vh-10rem)] bg-gradient-to-br from-background via-muted/30 to-background dark:from-background dark:via-muted/10 dark:to-background py-12 px-4 sm:px-6 lg:px-8 text-foreground">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center mb-8">
            <Link to="/" className="inline-block" aria-label="Vedas Trading Home">
                <UserPlus className="mx-auto h-14 w-14 text-primary" />
            </Link>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-gradient-maroon-to-gold">
                Create Your Account
            </h1>
            <p className="mt-3 text-muted-foreground">
                Join Vedas Trading to access exclusive insights and tools.
            </p>
        </div>

        <Card className="bg-card text-card-foreground shadow-2xl border border-border/30 dark:border-border/20">
          <CardHeader className="pt-8 pb-4 text-center">
            <CardDescription className="text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary hover:text-accent dark:text-accent dark:hover:text-primary transition-colors">
                Sign in
              </Link>
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {successMessage && (
                <div className="mb-4 text-sm text-green-600 bg-green-500/10 p-3 rounded-md border border-green-500/30">
                    {successMessage}
                </div>
            )}
            {error && (
                <div className="mb-4 text-sm text-destructive bg-destructive/10 p-3 rounded-md border border-destructive/30">
                    {error}
                </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="sr-only">Full Name</label>
                <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <User className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <Input
                    id="name" name="name" type="text" autoComplete="name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    className="pl-10 border-border focus:border-primary focus:ring-primary !bg-input text-foreground placeholder:text-muted-foreground/80"
                    placeholder="Full Name (Optional)" disabled={isSubmitting}
                    />
                </div>
              </div>

              <div>
                <label htmlFor="email-register" className="sr-only">Email address</label>
                <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Mail className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <Input
                    id="email-register" name="email" type="email" autoComplete="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-border focus:border-primary focus:ring-primary !bg-input text-foreground placeholder:text-muted-foreground/80"
                    placeholder="Email address" disabled={isSubmitting}
                    />
                </div>
              </div>

              <div>
                <label htmlFor="password-register" className="sr-only">Password</label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Lock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <Input
                    id="password-register" name="password" type="password" autoComplete="new-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 border-border focus:border-primary focus:ring-primary !bg-input text-foreground placeholder:text-muted-foreground/80"
                    placeholder="Password (min. 8 characters)" disabled={isSubmitting}
                    />
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Lock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <Input
                    id="confirm-password" name="confirmPassword" type="password" autoComplete="new-password" required
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 border-border focus:border-primary focus:ring-primary !bg-input text-foreground placeholder:text-muted-foreground/80"
                    placeholder="Confirm Password" disabled={isSubmitting}
                    />
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" className="w-full !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all cursor-pointer" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account <UserPlus className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <div className="text-center text-sm text-muted-foreground">
            By creating an account, you agree to our <br className="sm:hidden"/>
            <Link to="/terms-of-service" className="underline hover:text-primary dark:hover:text-accent transition-colors">Terms of Service</Link> and{' '}
            <Link to="/privacy-policy" className="underline hover:text-primary dark:hover:text-accent transition-colors">Privacy Policy</Link>.
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
